import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  input,
  Input,
  OnInit,
  Output,
  Renderer2,
  signal,
  ViewChild,
  InputSignal,
  computed,
  WritableSignal,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { hasReachedMaxLength, hasReachedMaxWords } from '@local/ts-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'u-textarea',
  templateUrl: './u-textarea.component.html',
  styleUrls: ['./u-textarea.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UTextareaComponent implements OnInit {
  readonly MAX_INPUT_WORDS_REACHED_MSG = 'Word limit reached';
  readonly MAX_NUMBER = 524288;
  private _autoResize = true;
  private _model: string;
  private _maxLength: number;

  isTitleFocused: boolean;
  doResize = false;
  isValidLength = signal(true);

  @Input() rows = 1;
  @Input() cols = 1;
  @Input() placeholder = '';
  @Input() styles: any = {};
  @Input() autofocus = true;
  @Input() disabled = false;
  @Input() ignoreEnter: boolean;
  @Input() readonly = false;
  @Input() disableAutoResize = true;
  @Input() maxWords: number;
  @Input() setMaxLength: boolean;
  @Input() truncateLines: boolean;
  @Input() maxRowLimit: number;
  @Input() minHeight = 24;
  @Input() fixedBackgroundColor: boolean;
  @Input() displayLimitWarning: boolean;
  @Input() label: string;
  @Input() errorBorder: boolean;
  @Input() errorText: string;
  readonly errorsTexts: InputSignal<{ [key: string]: string }> = input<{ [key: string]: string }>({});

  selectRegex: InputSignal<RegExp> = input(null);
  readonly formErrorText: WritableSignal<string | null> = signal(null);
  readonly fieldControl: InputSignal<AbstractControl> = input<AbstractControl>();
  readonly formControl = computed<FormControl | null>(() => {
    const control = this.fieldControl();
    return control instanceof FormControl ? (control as FormControl) : null;
  });
  readonly currentErrorText = computed(() => this.errorText || this.formErrorText());

  @Input()
  set autoResize(value: boolean) {
    this._autoResize = value;
    if (this._autoResize) {
      this.doResize = true;
    }
  }

  @Input() set maxLength(value: number) {
    this._maxLength = value;
    if (value && this.model) {
      this.validateTextInput(this.model);
    }
  }

  get maxLength(): number {
    return this._maxLength;
  }

  @Input() set model(value: string) {
    this._model = value;
    if (value) {
      this.validateTextInput(value);
    }
  }

  get model(): string {
    return this._model;
  }

  get autoResize(): boolean {
    return this._autoResize;
  }

  @ViewChild('textarea') textArea: ElementRef;

  @Output() onChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() onFocus: EventEmitter<any> = new EventEmitter<any>();
  @Output() onBlur: EventEmitter<any> = new EventEmitter<any>();
  @Output() onEnter: EventEmitter<any> = new EventEmitter<any>();
  @Output() handleValidationResult: EventEmitter<boolean> = new EventEmitter<boolean>();

  @HostBinding('style.--linesNumber') get linesNumber(): number {
    return this.rows;
  }

  constructor(
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!this.maxLength && !this.maxWords) {
      this.maxLength = this.rows * this.cols;
    }

    if (this.setMaxLength) {
      this.maxLength = this.MAX_NUMBER;
    }

    this.initErrorFormControl();
  }

  private initErrorFormControl() {
    const formControl = this.fieldControl();
    if (formControl) {
      const errors = formControl.errors;
      const shouldShowError = (formControl.touched || formControl.dirty) && errors;
      this.formErrorText.set(shouldShowError ? this.errorsTexts()?.[Object.keys(errors)[0]] || null : null);

      this.fieldControl()
        .statusChanges.pipe(untilDestroyed(this))
        .subscribe(() => {
          const errors = this.fieldControl().errors;
          this.formErrorText.set(errors ? this.errorsTexts()?.[Object.keys(errors)[0]] || null : null);
        });
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      //HACK to fix the textarea height issue caused by page refresh.
      this.handleAutoResize();
      const textareaEl = this.textArea?.nativeElement;
      if (textareaEl) {
        textareaEl.style.height = `${textareaEl.scrollHeight}px`;
      }
      if (this.autofocus) {
        this.setFocus();
      }
    }, 250);
  }

  setTextareaText(text: string) {
    this.model = text;
    if (this.selectRegex()) {
      this.setSelectedByPattern();
    }
    this.onChange.emit(this.model);
  }

  private setSelectedByPattern() {
    setTimeout(() => {
      const textareaEl = this.textArea?.nativeElement;
      const regex = this.selectRegex();
      const match = this.model.match(regex);
      if (match && match.index !== undefined) {
        const start = match.index;
        const end = start + match[0].length;
        textareaEl.setSelectionRange(start, end);
      }
      textareaEl.focus();
    }, 0);
  }

  setFocus(): void {
    this.renderer.selectRootElement(this.textArea.nativeElement).focus({ preventScroll: true });
    this.cdr.markForCheck();
  }

  private validateTextInput(text: string) {
    const isValidLength = !text.length || (!hasReachedMaxWords(text, this.maxWords) && !hasReachedMaxLength(text, this.maxLength));
    this.isValidLength.set(isValidLength);
    this.handleValidationResult.emit(isValidLength);
  }

  private hasReachedMaxRows(text?: string): boolean {
    if (!this.maxRowLimit) {
      return false;
    }
    const inputText = text || this.textArea.nativeElement.value;
    if (!inputText) {
      return false;
    }
    const rows = this.calculateRows();
    return rows > this.maxRowLimit;
  }

  onChangeEvent($event: string) {
    this.model = $event;

    this.onChange.emit($event);

    if ($event === '') {
      this.resetTextarea();
    }
    this.validateTextInput($event);
    this.handleAutoResize();
  }

  private handleAutoResize(text?: string) {
    if (!this.autoResize) {
      return;
    }
    this.doResize = !this.hasReachedMaxRows(text);
    this.cdr.markForCheck();
  }

  onFocusEvent($event) {
    this.isTitleFocused = true;
    this.scrollToEnd();
    this.onFocus.emit($event);
  }
  onBlurEvent($event) {
    this.isTitleFocused = false;
    this.scrollToStart();
    this.onBlur.emit($event);
  }
  onEnterEvent($event) {
    if (this.ignoreEnter) {
      return;
    }
    $event.stopPropagation();
    $event.preventDefault();
    this.onEnter.emit({ target: $event, value: this.model });
  }
  private calculateRows(): number {
    const textareaElement = this.textArea.nativeElement;
    const lineHeight = parseInt(getComputedStyle(textareaElement).lineHeight, 10);
    const scrollHeight = textareaElement.scrollHeight;
    return Math.floor(scrollHeight / lineHeight);
  }
  clearInput() {
    this.model = '';
    const control = this.fieldControl?.();
    if (control) {
      control.setValue('');
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity();
    }

    this.onChange.emit('');

    this.cdr.markForCheck();
    this.validateTextInput('');
    this.resetTextarea();
  }

  private resetTextarea() {
    this.textArea.nativeElement.value = '';
    this.doResize = true;
    this.textArea.nativeElement.style.height = `${this.minHeight}px`;
    this.cdr.markForCheck();
  }

  private scrollToStart() {
    const el: HTMLElement = this.textArea?.nativeElement;
    if (el) {
      el.scrollTop = 0;
    }
  }

  private scrollToEnd() {
    const el: HTMLElement = this.textArea?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Tab' && this.selectRegex()) {
      event.preventDefault();
      event.shiftKey ? this.selectPrevMatch() : this.selectNextMatch();
    }
  }

  private getSelectRegex(): RegExp | null {
    const pattern = this.selectRegex();
    if (!pattern) {
      return null;
    }
    try {
      return new RegExp(pattern, 'g');
    } catch {
      return null;
    }
  }

  private selectNextMatch() {
    const el = this.textArea?.nativeElement;
    const regex = this.getSelectRegex();
    const text = this.model;
    const pos = el.selectionEnd || 0;
    let match,
      found = false;
    regex.lastIndex = 0;
    while ((match = regex.exec(text))) {
      if (match.index > pos) {
        el.setSelectionRange(match.index, match.index + match[0].length);
        el.focus();
        found = true;
        break;
      }
    }
    if (!found) {
      regex.lastIndex = 0;
      match = regex.exec(text);
      if (match) {
        el.setSelectionRange(match.index, match.index + match[0].length);
        el.focus();
      }
    }
  }

  private selectPrevMatch() {
    const el = this.textArea?.nativeElement;
    const regex = this.getSelectRegex();
    const text = this.model;
    const pos = el.selectionStart || 0;
    let last = null,
      match;
    regex.lastIndex = 0;
    while ((match = regex.exec(text))) {
      if (match.index < pos) {
        last = match;
      } else {
        break;
      }
    }
    if (last) {
      el.setSelectionRange(last.index, last.index + last[0].length);
      el.focus();
    } else {
      let wrap = null;
      regex.lastIndex = 0;
      while ((match = regex.exec(text))) wrap = match;
      if (wrap) {
        el.setSelectionRange(wrap.index, wrap.index + wrap[0].length);
        el.focus();
      }
    }
  }

  handleTextareaClick() {
    const textareaEl = this.textArea?.nativeElement;
    const regex = this.getSelectRegex();
    if (!regex) {
      return;
    }
    const text = this.model;
    const cursorPos = textareaEl.selectionStart;

    let match;
    while ((match = regex.exec(text))) {
      const start = match.index;
      const end = start + match[0].length;
      if (cursorPos >= start && cursorPos <= end) {
        textareaEl.setSelectionRange(start, end);
        textareaEl.focus();
        break;
      }
    }
  }
}
